import { breakpointService } from './breakpointService';
import { imageResizerService } from './imageResizerService';
import { filenameInfoService } from './filenameInfoService';
import { conf } from './configService';
import { dateService } from './dateService';
import { stripTags, truncateText } from '../utils/stringUtils';
import { getContentModificationDate } from '../utils/contentUtils';

const getDomain = () =>
  conf.isStage
    ? `http://fng-globalsites-stage-html-current-${conf.siteCountryCode}.s3-website.eu-central-1.amazonaws.com`
    : `https://${conf.canonicalDomain}`;

const getOpenGraphType = bundle => {
  switch (bundle) {
    case 'author':
    case 'photographer':
    case 'error_403':
    case 'error_404':
    case 'homepage':
    case 'hub_page':
    case 'page':
    case 'pod_index':
    case 'search':
    case 'subscribe_page':
    case 'taxonomy':
    case 'television_schedule':
    case 'consent_management':
    case 'newsletter':
    case 'article':
    case 'photo_gallery':
    case 'vertical_slideshow_gallery':
    case 'show': {
      return 'article';
    }
    default: {
      throw new Error(`Content bundle "${bundle}" unknown.`);
    }
  }
};

export const metaDataServiceHelper = {
  getMetaTitle: (content, metaName, removeSuffix = false) => {
    let title = content.promoTitle || content.title || '';
    if (metaName && content.metaTags?.[metaName]) {
      title = content.metaTags[metaName];
    } else if (content.metaTags?.title) {
      if (content.metaTags.title.startsWith('| ')) {
        title = `${title} ${content.metaTags.title}`;
      } else {
        ({ title } = content.metaTags);
      }
    }
    if (title.includes(' | ')) {
      title = `${title} | National Geographic`;
    }
    if (removeSuffix) {
      title = title.replace(/ \|[^|]*$/i, '');
    }
    return title;
  },

  getMainImageEntity: content => {
    if (content.promoImage?.[0]?.entity?.mediaImage) {
      return content.promoImage[0].entity;
    }
    if (content.image?.entity?.mediaImage) {
      return content.image.entity;
    }
    return;
  },

  getDescription: content => {
    let description = '';
    if (content.metaTags?.description) {
      return stripTags(content.metaTags.description);
    }
    if (content.promoSummary?.value) {
      description = content.promoSummary.value;
    } else if (content.subHeadline) {
      description = content.subHeadline;
    } else if (content.synopsis?.value) {
      // For Shows.
      description = content.synopsis.value;
    } else if (content.images?.[0]?.entity?.caption) {
      // For photoGalleries.
      description = content.images[0].entity.caption;
    } else if (content.mainContent) {
      if (content.mainContent.length > 0) {
        content.mainContent.map(paragraph => {
          if (
            paragraph.entity?.type &&
            paragraph.entity.type === 'ParagraphContent' &&
            paragraph.entity.content?.value &&
            description === ''
          ) {
            description = paragraph.entity.content.value;
          }
          return;
        });
      }
    }
    if (
      (description === '' || !description) &&
      content.promoImage?.[0]?.entity?.caption
    ) {
      description = content.promoImage[0].entity.caption;
    }
    if (description === '' || !description) {
      description = conf.defaultMetadataDescription;
    }
    if (description) {
      return truncateText(stripTags(description), 160);
    }
    return null;
  }
};

export const metaDataService = {
  getContentMetaData: content => {
    const title = metaDataServiceHelper.getMetaTitle(content);
    return {
      // Ensure that all contents on this site are of the same language
      language: conf.langcode,
      title,
      meta: metaDataService.getAllMeta(content),
      link: metaDataService.getLink(content)
    };
  },

  getAllMeta: content => {
    return metaDataService
      .getStandardMeta(content)
      .concat(metaDataService.getOpenGraphMeta(content))
      .concat(metaDataService.getOpenGraphMetaImage(content))
      .concat(metaDataService.getTwitterMeta(content))
      .concat(metaDataService.getArticleMeta(content))
      .concat(metaDataService.getSponsoredMeta(content));
  },

  getStandardMeta: content => {
    const contentChangedIso = new Date(
      getContentModificationDate(content) * 1000
    )
      .toISOString()
      .replace('.000Z', 'Z');

    const standardMeta = [
      {
        name: 'title',
        content:
          content.metaTags?.title || metaDataServiceHelper.getMetaTitle(content)
      },
      {
        name: 'date',
        scheme: 'W3CDTF',
        content: contentChangedIso
      },
      {
        name: 'publicationDate',
        content: dateService.format(
          getContentModificationDate(content) * 1000,
          'd/m/Y'
        )
      }
    ];
    const description = metaDataServiceHelper.getDescription(content);
    if (description) {
      standardMeta.push({
        name: 'description',
        content: description
      });
    }

    if (content.metaTags?.keywords) {
      standardMeta.push({
        name: 'keywords',
        content: content.metaTags.keywords
      });
    }

    if (content.primaryTaxonomy?.entity?.name) {
      standardMeta.push({
        name: 'localPrimaryTerm',
        content: content.primaryTaxonomy.entity.name
      });
    }

    if (content.primaryTaxonomy?.entity?.englishName) {
      standardMeta.push({
        name: 'englishPrimaryTerm',
        content: content.primaryTaxonomy.entity.englishName
      });
    }

    return standardMeta;
  },

  getOpenGraphMeta: content => {
    const openGraphMeta = [
      {
        property: 'og:type',
        content: getOpenGraphType(content.bundle)
      },
      {
        property: 'og:url',
        content: `${getDomain()}${content.url?.path}`
      },
      {
        property: 'og:title',
        content: metaDataServiceHelper.getMetaTitle(content, 'og:title', true)
      },
      {
        property: 'og:updated_time',
        content: new Date(getContentModificationDate(content) * 1000)
          .toISOString()
          .replace('.000Z', 'Z')
      }
    ];

    const description =
      content.metaTags?.['og:description'] ||
      metaDataServiceHelper.getDescription(content);
    if (description) {
      openGraphMeta.push({
        property: 'og:description',
        content: stripTags(description)
      });
    }

    return openGraphMeta;
  },
  getOpenGraphMetaImage: content => {
    const openGraphMetaImage = [];
    const mainImageEntity = metaDataServiceHelper.getMainImageEntity(content);
    if (mainImageEntity) {
      // There are some images that doesn't exist on the filesystem but
      // are inside Drupal's database, and their dimensions are 0
      if (
        mainImageEntity.mediaImage.width &&
        mainImageEntity.mediaImage.height &&
        mainImageEntity.mediaImage.url
      ) {
        const desktopStyleData = imageResizerService.getResizedImage(
          mainImageEntity.mediaImage,
          breakpointService.getLargeDesktopBreakpoint().width,
          {
            position: mainImageEntity.focalPoint,
            quality: imageResizerService.getImageEntityQuality(
              mainImageEntity.quality
            )
          }
        );

        openGraphMetaImage.push({
          property: 'og:image',
          content: desktopStyleData.url
        });

        openGraphMetaImage.push({
          property: 'og:image:alt',
          content: metaDataServiceHelper.getMetaTitle(content, undefined, true)
        });

        openGraphMetaImage.push({
          property: 'og:image:width',
          content: desktopStyleData.width
        });

        openGraphMetaImage.push({
          property: 'og:image:height',
          content: desktopStyleData.height
        });
        openGraphMetaImage.push({
          property: 'og:image:type',
          content: filenameInfoService.getMimeType(desktopStyleData.url)
        });
      }
    }
    return openGraphMetaImage;
  },
  getTwitterMeta: content => {
    const ogTitle = metaDataServiceHelper.getMetaTitle(
      content,
      'og:title',
      true
    );
    const twitterTitle = metaDataServiceHelper.getMetaTitle(
      content,
      'twitter:title',
      true
    );
    const twitterMeta = [];
    if (twitterTitle !== ogTitle) {
      twitterMeta.push({
        property: 'twitter:title',
        content: twitterTitle
      });
    }
    if (content.metaTags?.['twitter:image']) {
      twitterMeta.push({
        property: 'twitter:image',
        content: content.metaTags['twitter:image']
      });
    }
    if (content.metaTags?.['twitter:image:alt']) {
      twitterMeta.push({
        property: 'twitter:image:alt',
        content: content.metaTags['twitter:image:alt']
      });
    }

    return twitterMeta;
  },

  getArticleMeta: content => {
    const articleMeta = [
      {
        property: 'article:modified_time',
        content: new Date(getContentModificationDate(content) * 1000)
          .toISOString()
          .replace('.000Z', 'Z')
      }
    ];

    if (content.created) {
      articleMeta.push({
        property: 'article:published_time',
        content: new Date(content.created * 1000)
          .toISOString()
          .replace('.000Z', 'Z')
      });
    }

    if (content.primaryTaxonomy?.entity?.name) {
      articleMeta.push({
        property: 'article:section',
        content: content.primaryTaxonomy.entity.name
      });
    }

    if (content.metaTags?.news_keywords) {
      // Remove possible duplicates
      const tags = [...new Set(content.metaTags.news_keywords.split(','))];
      tags.forEach(tag => {
        articleMeta.push({
          property: 'article:tag',
          content: tag.trim()
        });
      });
    }

    return articleMeta;
  },

  getLink: content => {
    // Link definitions.
    const links = [
      {
        rel: 'canonical',
        href: `${getDomain()}${content.url?.path}`
      }
    ];

    const mainImageEntity = metaDataServiceHelper.getMainImageEntity(content);
    if (mainImageEntity) {
      links.push({
        rel: 'image_src',
        href: imageResizerService.getStyleUrl(
          mainImageEntity.mediaImage.url,
          breakpointService.getLargeDesktopBreakpoint().width,
          undefined,
          { position: mainImageEntity.focalPoint }
        )
      });
    }

    return links;
  },

  getSponsoredMeta: content => {
    return content.branded
      ? [
          {
            property: 'cr:sponsored'
          },
          {
            property: 'branded_content_campaign',
            content: content.brandedCampaign
          }
        ]
      : [];
  }
};
